import api from './axiosConfig';

export interface User {
    id: string;
    email: string;
    username: string;
    is_active: boolean;
    profile_picture: string;
    created_at: string;
    roles: string[];
    last_updated: string;
    last_access_at: string | null;
}

export interface UserResponse {
    data: User[];
    total: number;
    page: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
}

export interface UserFilters {
    page?: number;
    limit?: number;
    search?: string;
    is_active?: boolean;
    role?: string;
}

export interface BulkActionRequest {
    user_ids: string[];
    action: 'block' | 'unblock' | 'assign_role' | 'remove_role';
    role?: string[];
}

export interface CreateUserRequest {
    username: string;
    email: string;
    password: string;
    is_active?: boolean;
    roles: string[];
    profile_picture?: File | null;
}

export interface UpdateUserRequest {
    username?: string;
    email?: string;
    password?: string;
    is_active?: boolean;
    roles?: string[];
}

export interface GetUserResponse {
    email: string;
    username: string;
    is_active: boolean;
    profile_picture: string;
    id: string;
    created_at: string;
    roles: string[];
    last_updated: string;
    last_access_at: string;
}

export interface MyAccountResponse {
    id: string;
    email: string;
    username: string;
    is_active: boolean;
    profile_picture: string;
    roles: string[];
}

export interface ChangePasswordRequest {
    old_password: string;
    new_password: string;
    confirm_new_password: string;
}

export const userService = {
    getUsers: async (filters: UserFilters = {}): Promise<UserResponse> => {
        try {
            const params = new URLSearchParams();
            
            // Add filters to query parameters
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });

            const response = await api.get<UserResponse>(`/users/?${params.toString()}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Create user method with multipart form data
    createUser: async (userData: CreateUserRequest): Promise<any> => {
        try {
            const formData = new FormData();
            
            // Add required fields
            formData.append('username', userData.username);
            formData.append('email', userData.email);
            formData.append('password', userData.password);
            
            // Add optional fields
            if (userData.is_active !== undefined) {
                formData.append('is_active', userData.is_active.toString());
            }
            
            // Add roles array
            userData.roles.forEach(role => {
                formData.append('roles', role);
            });
            
            // Add profile picture if provided
            if (userData.profile_picture) {
                formData.append('profile_picture', userData.profile_picture);
            }

            const response = await api.post('/users/create', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get single user by ID
    getUser: async (userId: string): Promise<GetUserResponse> => {
        try {
            const response = await api.get<GetUserResponse>(`/users/get-user/${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Update user method with multipart form data
    updateUser: async (userId: string, userData: UpdateUserRequest): Promise<any> => {
        try {
            const formData = new FormData();
            
            // Add fields that are provided
            if (userData.username !== undefined) {
                formData.append('username', userData.username);
            }
            if (userData.email !== undefined) {
                formData.append('email', userData.email);
            }
            if (userData.password !== undefined) {
                formData.append('password', userData.password);
            }
            if (userData.is_active !== undefined) {
                formData.append('is_active', userData.is_active.toString());
            }
            
            // Add roles array if provided
            if (userData.roles && userData.roles.length > 0) {
                userData.roles.forEach(role => {
                    formData.append('roles', role);
                });
            }

            const response = await api.patch(`/users/update/${userId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Upload profile picture method
    uploadProfilePicture: async (userId: string, file: File): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post(`/users/${userId}/upload-profile-picture`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Bulk action method
    bulkAction: async (request: BulkActionRequest): Promise<any> => {
        try {
            const response = await api.post('/users/bulk-action', request);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Helper method to map API role names to display names
    mapRoleToDisplay: (role: string): string => {
        const roleMappings: Record<string, string> = {
            'gensize_admin': 'Gensize Admin',
            'gensize_data_admin': 'Gensize Admin',
            'gensize_data_worker': 'Gensize Data Worker',
            'gensize_data_approver': 'Gensize Data Approver',
            'genisze_data_worker': 'Gensize Data Worker', // Handle typo in API
            'genisze_admin': 'Gensize Admin', // Handle typo in API
        };

        return roleMappings[role.toLowerCase()] || role;
    },

    // Helper method to map display names back to API role names
    mapDisplayToRole: (displayRole: string): string => {
        const reverseRoleMappings: Record<string, string> = {
            'Gensize Admin': 'gensize_admin',
            'Gensize Data Worker': 'gensize_data_worker',
            'Gensize Data Approver': 'gensize_data_approver',
        };

        return reverseRoleMappings[displayRole] || displayRole;
    },

    // Helper method to map array of roles
    mapRolesToDisplay: (roles: string[]): string[] => {
        return roles.map(role => userService.mapRoleToDisplay(role));
    },

    // Helper method to map array of display roles back to API roles
    mapDisplayRolesToApi: (displayRoles: string[]): string[] => {
        return displayRoles.map(role => userService.mapDisplayToRole(role));
    },

    // Helper method to format date for display
    formatDate: (dateString: string | null): string => {
        if (!dateString) {
            return 'No access';
        }
        
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else if (diffInMinutes > 0) {
            return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    },

    // Helper method to format time difference for "Last Changed"
    formatTimeDifference: (dateString: string): string => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const diffInMonths = Math.floor(diffInDays / 30);
        const diffInYears = Math.floor(diffInDays / 365);

        if (diffInYears > 0) {
            return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ${diffInMonths % 12} month${(diffInMonths % 12) > 1 ? 's' : ''}`;
        } else if (diffInMonths > 0) {
            return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ${diffInDays % 30} day${(diffInDays % 30) > 1 ? 's' : ''}`;
        } else if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''}`;
        } else {
            return 'Today';
        }
    },

    // Get current user's account information
    getMyAccount: async (): Promise<MyAccountResponse> => {
        try {
            const response = await api.get<MyAccountResponse>('/users/my_account');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Change password for current user
    changePassword: async (passwordData: ChangePasswordRequest): Promise<any> => {
        try {
            const response = await api.post('/users/my-account/change-password', passwordData);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}; 